'use client';

import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import ThemeToggle from './ThemeToggle';
import Icon from './Icon';
import { display } from '../lib/fonts';

export const UserContext = createContext({
  user: null,
  loading: true,
  isLoggingOut: false,
  updateUserProfile: () => { },
  updateUserProfileName: () => { },
  handleLogout: () => { }
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const updateUserProfile = (userData) => {
    setUser(userData);
  };

  const updateUserProfileName = (newName) => {
    setUser(prevUser => ({
      ...prevUser,
      name: newName
    }));
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/validate-token', {
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Auth check in Navigation:', data);

          if (data.authenticated && data.user) {
            setUser(data.user);

            // Clean up any localStorage userData since we're using JWT tokens
            if (typeof window !== 'undefined') {
              localStorage.removeItem('userData');
            }

            // Redirect based on role if on login pages
            const path = window.location.pathname;
            if (path === '/job-seeker-login' || path === '/referrer-login') {
              const dashboardPath = data.user.role === 'referrer'
                ? '/referrer-dashboard'
                : '/job-seeker-dashboard';
              window.location.href = dashboardPath;
            }
          } else {
            // Check if account was deleted
            if (data.errorDetails?.accountDeleted) {
              console.log('Account deleted detected:', data);

              // Show user-friendly message
              if (typeof window !== 'undefined') {
                alert('Your account has been deleted. You have been logged out.');
              }

              // Clear any stored data
              localStorage.removeItem('userData');
              setUser(null);

              // Redirect to welcome page
              if (window.location.pathname !== '/') {
                window.location.href = '/';
              }
              return;
            }

            // If JWT token is not valid, check if there's userData in localStorage (legacy signup flow)
            if (typeof window !== 'undefined') {
              const storedUserData = localStorage.getItem('userData');
              if (storedUserData) {
                try {
                  const userData = JSON.parse(storedUserData);
                  console.log('Found userData in localStorage:', userData);

                  // Validate the stored user data by making a request to validate-token
                  // This will help ensure the user is still authenticated
                  const validateResponse = await fetch('/api/validate-token', {
                    credentials: 'include',
                    headers: {
                      'Cache-Control': 'no-cache',
                      'Pragma': 'no-cache'
                    }
                  });

                  if (validateResponse.ok) {
                    const validateData = await validateResponse.json();
                    if (validateData.authenticated && validateData.user) {
                      // JWT token is valid, use the server data
                      setUser(validateData.user);
                      localStorage.removeItem('userData'); // Clean up localStorage
                    } else {
                      // Check if account was deleted
                      if (validateData.errorDetails?.accountDeleted) {
                        console.log('Account deleted detected in localStorage check:', validateData);

                        // Show user-friendly message
                        if (typeof window !== 'undefined') {
                          alert('Your account has been deleted. You have been logged out.');
                        }

                        // Clear localStorage and redirect
                        localStorage.removeItem('userData');
                        setUser(null);
                        if (window.location.pathname !== '/') {
                          window.location.href = '/';
                        }
                        return;
                      }

                      // JWT token is invalid, clear localStorage and redirect to login
                      localStorage.removeItem('userData');
                      setUser(null);
                      if (window.location.pathname !== '/' &&
                        window.location.pathname !== '/job-seeker-login' &&
                        window.location.pathname !== '/referrer-login') {
                        window.location.href = '/';
                      }
                    }
                  } else {
                    // API call failed, clear localStorage and redirect to login
                    localStorage.removeItem('userData');
                    setUser(null);
                    if (window.location.pathname !== '/' &&
                      window.location.pathname !== '/job-seeker-login' &&
                      window.location.pathname !== '/referrer-login') {
                      window.location.href = '/';
                    }
                  }
                } catch (error) {
                  console.error('Error parsing stored userData:', error);
                  localStorage.removeItem('userData');
                  setUser(null);
                }
              } else {
                setUser(null);
              }
            } else {
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error('Auth check error in Navigation:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Set up a timer to check auth status periodically
    const authCheckInterval = setInterval(checkAuth, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(authCheckInterval);
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Force clear all storage and cookies
      if (typeof window !== 'undefined') {
        console.log('🧹 Force clearing all storage and cookies...');

        // Clear all localStorage items individually first
        const localStorageKeys = Object.keys(localStorage);
        localStorageKeys.forEach(key => {
          console.log(`🗑️ Removing localStorage key: ${key}`);
          localStorage.removeItem(key);
        });

        // Clear all sessionStorage items individually first
        const sessionStorageKeys = Object.keys(sessionStorage);
        sessionStorageKeys.forEach(key => {
          console.log(`🗑️ Removing sessionStorage key: ${key}`);
          sessionStorage.removeItem(key);
        });

        // Force clear all cookies with multiple domain/path combinations
        const cookies = document.cookie.split(';');

        // Use the same domain logic as the backend
        const currentHostname = window.location.hostname;
        const isProduction = currentHostname !== 'localhost';
        const domains = [currentHostname, `.${currentHostname}`];
        // If it's a subdomain, also try to clear on the apex domain if possible
        const parts = currentHostname.split('.');
        if (parts.length > 2) {
          const apex = parts.slice(-2).join('.');
          domains.push(apex, `.${apex}`);
        }
        domains.push(''); // No domain attribute covers current host

        const paths = ['/', '/api', '/auth', ''];

        cookies.forEach(cookie => {
          const [name] = cookie.trim().split('=');
          if (name) {
            console.log(`🍪 Clearing cookie: ${name}`);

            // Clear cookie with different domain and path combinations
            domains.forEach(domain => {
              paths.forEach(path => {
                if (isProduction && domain) {
                  // Production: Use secure cookies
                  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; secure; samesite=lax`;
                  document.cookie = `${name}=; max-age=0; path=${path}; domain=${domain}; secure; samesite=lax`;
                } else {
                  // Localhost or no domain: No secure flag required if not HTTPS
                  const secureFlag = window.location.protocol === 'https:' ? 'secure;' : '';
                  const domainAttr = domain ? `domain=${domain};` : '';
                  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; ${domainAttr} ${secureFlag} samesite=lax`;
                  document.cookie = `${name}=; max-age=0; path=${path}; ${domainAttr} ${secureFlag} samesite=lax`;
                }
              });
            });
          }
        });

        // Clear specific known cookies with the same logic
        const knownCookies = [
          'token', 'session', 'sessionId', 'auth-token',
          'next-auth.session-token', '__Secure-next-auth.session-token',
          'userData', 'signupEmail', 'auth', 'login', 'user'
        ];

        knownCookies.forEach(cookieName => {
          domains.forEach(domain => {
            paths.forEach(path => {
              if (isProduction) {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; secure; samesite=lax`;
                document.cookie = `${cookieName}=; max-age=0; path=${path}; domain=${domain}; secure; samesite=lax`;
              } else {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; samesite=lax`;
                document.cookie = `${cookieName}=; max-age=0; path=${path}; samesite=lax`;
              }
            });
          });
        });

        console.log('✅ All storage and cookies cleared');
      }

      // Call the logout API
      const response = await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('🔍 Logout API response status:', response.status);

      // Check if account was deleted
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.accountDeleted) {
          console.log('Account was deleted, showing notification');
          if (typeof window !== 'undefined') {
            alert('Your account has been deleted. You have been logged out.');
          }
        }
      }

      // Clear local user state immediately
      setUser(null);

      // Force clear any remaining state
      if (typeof window !== 'undefined') {
        // Clear any remaining localStorage
        localStorage.clear();
        sessionStorage.clear();

        // Clear any remaining cookies one more time with correct domain
        const currentHostname = window.location.hostname;
        const isProduction = currentHostname !== 'localhost';
        const domains = [currentHostname, `.${currentHostname}`];
        // If it's a subdomain, also try to clear on the apex domain if possible
        const parts = currentHostname.split('.');
        if (parts.length > 2) {
          const apex = parts.slice(-2).join('.');
          domains.push(apex, `.${apex}`);
        }
        domains.push(''); // No domain attribute covers current host

        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          if (name) {
            domains.forEach(domain => {
              if (isProduction) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; secure; samesite=lax`;
              } else {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; samesite=lax`;
              }
            });
          }
        });
      }

      console.log('🔄 Redirecting to home page...');

      // Add a small delay to ensure state is cleared before redirect
      setTimeout(() => {
        // Force a full page reload to ensure all state is cleared
        // Use window.location.replace to prevent the back button from returning to the logged-in state
        window.location.replace('/');
      }, 100);

      // If redirect didn't work (which it should), log the response
      if (!response.ok) {
        console.error('Logout API call failed:', await response.text());
      }
    } catch (error) {
      console.error('Logout error:', error);

      // Even if there was an error, force clear everything and redirect
      setUser(null);

      if (typeof window !== 'undefined') {
        localStorage.clear();
        sessionStorage.clear();

        // Clear cookies with correct domain configuration
        const currentHostname = window.location.hostname;
        const isProduction = currentHostname !== 'localhost';
        const domains = [currentHostname, `.${currentHostname}`];
        // If it's a subdomain, also try to clear on the apex domain if possible
        const parts = currentHostname.split('.');
        if (parts.length > 2) {
          const apex = parts.slice(-2).join('.');
          domains.push(apex, `.${apex}`);
        }
        domains.push(''); // No domain attribute covers current host

        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          if (name) {
            domains.forEach(domain => {
              if (isProduction) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; secure; samesite=lax`;
              } else {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; samesite=lax`;
              }
            });
          }
        });
      }

      // Still redirect even if there was an error to ensure user is logged out
      setTimeout(() => {
        window.location.replace('/');
      }, 100);
    }
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    return user.role === 'referrer' ? '/referrer-dashboard' : '/job-seeker-dashboard';
  };

  const isCurrentPath = (path) => {
    // Use window.location for path checking
    if (typeof window !== 'undefined') {
      return window.location.pathname === path;
    }
    return false;
  };

  return (
    <UserContext.Provider value={{
      user,
      loading,
      isLoggingOut,
      updateUserProfile,
      updateUserProfileName,
      handleLogout
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default function Navigation() {
  const { user, loading, handleLogout } = useContext(UserContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Lock page scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen]);

  useEffect(() => {
    // Simple mobile detection
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  const handleAIClick = useCallback(async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/validate-token');
      const data = await response.json();
      if (!response.ok || !data.authenticated) {
        setShowAuthMessage(true);
        setTimeout(() => {
          router.push('/login');
        }, 1500);
      } else {
        router.push('/AI_mock_interview');
      }
    } catch (error) {
      setShowAuthMessage(true);
      setTimeout(() => {
        router.push('/job-seeker-login');
      }, 1500);
    } finally {
      closeMobileNav();
    }
  }, [router]);

  const closeMobileNav = () => {
    setMobileNavOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    return user.role === 'referrer' ? '/referrer-dashboard' : '/job-seeker-dashboard';
  };

  const isCurrentPath = (path) => {
    if (typeof window !== 'undefined') {
      return window.location.pathname === path;
    }
    return false;
  };

  const navLinks = [
    { href: '/job_search', label: 'Job Search' },
    { href: '/AI_mock_interview', label: 'AI Interview', onClick: handleAIClick },
    { href: '/blog', label: 'Blog' },
    ...(user ? [{ href: getDashboardLink(), label: 'Dashboard' }] : []),
  ];

  const drawerLinks = [
    { href: '/', label: 'Home' },
    { href: '/job_search', label: 'Job Search' },
    { href: '/AI_mock_interview', label: 'AI Interview' },
    { href: '/blog', label: 'Blog' },
    { href: '/faq', label: 'FAQ' },
    ...(user ? [{ href: getDashboardLink(), label: 'Dashboard' }] : []),
  ];

  const isActive = (href) =>
    !!pathname && (pathname === href || (href !== '/' && pathname.startsWith(`${href}/`)));

  const Wordmark = ({ className = '' }) => (
    <span className={`${display.className} text-xl font-extrabold tracking-tight text-slate-900 dark:text-white ${className}`}>
      Doogler<span className="text-customBlue">.</span>
    </span>
  );

  const iconBtn = 'flex h-10 w-10 items-center justify-center rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-customBlue';

  const profileMenu = (width, signOutLabel) => (
    <div className={`absolute right-0 mt-2 ${width} overflow-hidden rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg`}>
      <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800">
        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user.name}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
        {user.role && (
          <p className="mt-1.5 inline-flex rounded-full bg-slate-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">{user.role}</p>
        )}
      </div>
      <div className="p-1.5">
        <div
          onClick={(e) => {
            // Clicking the row triggers the theme toggle button inside it
            const btn = e.currentTarget.querySelector('button');
            if (btn) btn.click();
          }}
          className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-lg px-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
        >
          <span>Theme</span>
          <ThemeToggle />
        </div>
        <button
          onClick={() => { setProfileDropdownOpen(false); handleLogout(); }}
          className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" /></svg>
          {signOutLabel}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Main navigation */}
      <nav role="navigation" aria-label="Main" itemScope itemType="https://schema.org/SiteNavigationElement"
        className="sticky top-0 z-40 border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="relative mx-auto flex h-14 md:h-16 md:grid md:grid-cols-[1fr_auto_1fr] w-full max-w-7xl items-center justify-between gap-4 px-3 sm:px-6 lg:px-8">

          {/* Left: menu button (mobile) / logo + links (desktop) */}
          <div className="flex items-center gap-2 md:gap-8">
            <button onClick={() => setMobileNavOpen(true)} className={`${iconBtn} md:hidden`} aria-label="Open menu" aria-expanded={mobileNavOpen}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link href="/" className="hidden md:inline-flex items-center hover:opacity-80 transition-opacity" aria-label="Doogler home">
              <Wordmark className="text-2xl" />
            </Link>

          </div>

            <div className="hidden md:flex items-center justify-center gap-1">
              {navLinks.map(item => (
                <Link key={item.href} href={item.href} itemProp="url" onClick={item.onClick}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  className={`relative rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-slate-900 dark:text-white after:absolute after:inset-x-3 after:-bottom-[17px] after:h-0.5 after:rounded-full after:bg-customBlue'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  }`}>
                  <span itemProp="name">{item.label}</span>
                </Link>
              ))}
            </div>

          {/* Centered logo (mobile) */}
          <Link href="/" className="md:hidden absolute left-1/2 -translate-x-1/2" aria-label="Doogler home">
            <Wordmark />
          </Link>

          {/* Right: desktop */}
          <div className="hidden md:flex items-center justify-end gap-2">
            {loading ? (
              <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200 dark:bg-gray-800" />
            ) : user ? (
              <div className="flex items-center gap-3">
                <span className="hidden lg:block text-sm text-slate-500 dark:text-slate-400">
                  Hi, <span className="font-semibold text-slate-900 dark:text-white">{user.name}</span>
                </span>
                <div className="relative profile-dropdown-container">
                  <button onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    aria-label="Account menu" aria-expanded={profileDropdownOpen}
                    className="w-9 h-9 rounded-full bg-customBlue flex items-center justify-center text-sm font-bold text-slate-950 ring-2 ring-white dark:ring-gray-950 hover:brightness-95 transition">
                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                  </button>
                  {profileDropdownOpen && profileMenu('w-56', 'Sign Out')}
                </div>
              </div>
            ) : (
              <>
                <ThemeToggle />
                <Link href="/login"
                  className="inline-flex min-h-[40px] items-center rounded-lg px-3.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors">
                  Sign In
                </Link>
                <Link href="/sign-up"
                  className="inline-flex min-h-[40px] items-center rounded-lg bg-customBlue px-4 text-sm font-semibold text-slate-950 hover:brightness-95 transition">
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Right: mobile */}
          <div className="md:hidden flex items-center gap-1 profile-dropdown-container">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  aria-label="Account menu" aria-expanded={profileDropdownOpen}
                  className="h-10 w-10 rounded-full bg-customBlue flex items-center justify-center text-base font-bold text-slate-950 active:scale-95 transition-transform"
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                </button>
                {profileDropdownOpen && profileMenu('w-52', 'Logout')}
              </div>
            ) : (
              <>
                <ThemeToggle />
                <Link href="/sign-up" className="inline-flex min-h-[40px] items-center rounded-lg bg-customBlue px-3.5 text-sm font-semibold text-slate-950 active:scale-95 transition-transform">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-[60] flex w-[min(20rem,85vw)] flex-col border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-transform duration-300 ease-in-out md:hidden ${mobileNavOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`}
        aria-hidden={!mobileNavOpen}
      >
        <div className="flex h-14 items-center justify-between border-b border-slate-200 dark:border-gray-800 px-4">
          <Link href="/" onClick={closeMobileNav} aria-label="Doogler home"><Wordmark /></Link>
          <button onClick={closeMobileNav} className={iconBtn} aria-label="Close menu">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3" aria-label="Mobile">
          {drawerLinks.map(item => (
            <Link key={item.href} href={item.href} onClick={closeMobileNav}
              aria-current={isActive(item.href) ? 'page' : undefined}
              tabIndex={mobileNavOpen ? 0 : -1}
              className={`flex min-h-[48px] items-center rounded-xl px-4 text-base font-medium transition-colors ${
                isActive(item.href)
                  ? 'bg-teal-50 dark:bg-customBlue/10 text-teal-800 dark:text-customBlue'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-900'
              }`}>
              {item.label}
            </Link>
          ))}
        </nav>

        {!user && (
          <div className="space-y-2 border-t border-slate-200 dark:border-gray-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <Link href="/sign-up" onClick={closeMobileNav} tabIndex={mobileNavOpen ? 0 : -1}
              className="flex min-h-[48px] w-full items-center justify-center rounded-xl bg-customBlue text-base font-semibold text-slate-950 transition active:scale-[0.98]">
              Create Account
            </Link>
            <Link href="/login" onClick={closeMobileNav} tabIndex={mobileNavOpen ? 0 : -1}
              className="flex min-h-[48px] w-full items-center justify-center rounded-xl border border-slate-300 dark:border-gray-700 text-base font-semibold text-slate-800 dark:text-slate-100 transition hover:bg-slate-50 dark:hover:bg-gray-900">
              Sign In
            </Link>
          </div>
        )}
      </div>

      {/* Overlay for mobile menu */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-[55] bg-slate-950/50 backdrop-blur-[2px] md:hidden"
          onClick={closeMobileNav}
          aria-hidden="true"
        ></div>
      )}

      {showAuthMessage && (
        <div role="alert" className="fixed left-1/2 top-4 z-[9999] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center justify-between rounded-xl bg-slate-900 px-5 py-3 text-sm text-white shadow-lg">
          <span className="flex-1">Please Sign in/Sign Up to access AI Interview</span>
          <button
            className="ml-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-white focus:outline-none"
            onClick={() => setShowAuthMessage(false)}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}

      {/* Mobile bottom navigation (signed-in only) */}
      {user && (
        <>
          <nav
            aria-label="Primary"
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex h-[calc(4rem+env(safe-area-inset-bottom))] items-stretch border-t border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-1 pb-[env(safe-area-inset-bottom)]"
          >
            {[
              { href: '/', label: 'Home', icon: 'home' },
              { href: '/job_search', label: 'Search', icon: 'search' },
              user?.role === 'referrer'
                ? { href: '/manage-jobs', label: 'Post job', icon: 'plus' }
                : { href: '/browse-referral-jobs', label: 'Referrals', icon: 'users' },
              { href: '/AI_mock_interview', label: 'Interview', icon: 'chip' },
              { href: getDashboardLink(), label: 'Dashboard', icon: 'grid' },
            ].map((tab) => {
              const active = isActive(tab.href);
              return (
                <Link
                  key={tab.href}
                  href={tab.href}
                  aria-current={active ? 'page' : undefined}
                  className={`flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                    active ? 'text-teal-700 dark:text-customBlue' : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  <Icon name={tab.icon} className="h-6 w-6" strokeWidth={active ? 2.2 : 1.8} />
                  {tab.label}
                </Link>
              );
            })}
          </nav>

          <style jsx global>{`
            @media (max-width: 767px) {
              body { padding-bottom: calc(64px + env(safe-area-inset-bottom)) !important; }
            }
          `}</style>
        </>
      )}
    </>
  );
}
